import { useState } from 'react';

import { FixtureList } from './fixture-list';
import { SquadEditor } from './squad-editor';
import { useHarnessBuilder } from './use-harness-builder';
import { setActiveChip } from '@lib/live/harness/edit';
import {
  SQUAD_QUERY_PARAM,
  encodeHarnessSquad,
  writeStoredHarnessSquad
} from '@lib/live/harness/storage';
import type { HarnessSquad } from '@lib/live/harness/squad';
import type { FplActiveChip } from '@lib/live/team/types';

type LiveSimulationHarnessProps = {
  accessToken: string;
  /** Gameweek the fixture list opens on. The next one, pre-season. */
  defaultGameweek: number;
  /** The squad currently driving the pitch, if any. */
  activeSquad: HarnessSquad | null;
};

const CHIPS: readonly { value: FplActiveChip; label: string }[] = [
  { value: null, label: 'No chip' },
  { value: 'bboost', label: 'Bench boost' },
  { value: '3xc', label: 'Triple captain' }
];

const toChip = (value: string): FplActiveChip =>
  CHIPS.find((chip) => String(chip.value) === value)?.value ?? null;

/**
 * Applying a squad reloads the page rather than pushing state.
 *
 * The provider builds its match subscriptions from the squad, and swapping one
 * squad for another mid-session would tear down and re-attach every channel
 * while the old ones were still settling. A reload is the honest way to start
 * a fresh attachment, and this is an internal tool where a page load costs
 * nothing.
 */
const applySquad = (squad: HarnessSquad | null): void => {
  writeStoredHarnessSquad(squad);
  const url = new URL(window.location.href);
  if (squad === null) {
    url.searchParams.delete(SQUAD_QUERY_PARAM);
  } else {
    url.searchParams.set(SQUAD_QUERY_PARAM, encodeHarnessSquad(squad));
  }
  window.location.replace(url.toString());
};

/**
 * Internal tool for building a fabricated FPL team against chosen fixtures, so
 * live mode can be exercised without waiting for a matchday.
 *
 * Visible to anyone the harness is available to, which is a deliberate change
 * from an earlier design that kept it hidden until activated. Access is
 * already restricted to staff accounts on non-production builds, so obscurity
 * on top of that protects nobody, and a hidden panel cannot be used to build a
 * first squad: that would need a hand-encoded URL, which is a worse tool and a
 * worse failure mode. See `docs/live-simulation-harness.md`.
 */
export const LiveSimulationHarness = ({
  accessToken,
  defaultGameweek,
  activeSquad
}: LiveSimulationHarnessProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const builder = useHarnessBuilder(accessToken, defaultGameweek);
  const { draft, problems, shortfall } = builder;

  const shortfallText =
    shortfall === null
      ? null
      : Object.entries(shortfall)
          .filter(([, missing]) => missing > 0)
          .map(([position, missing]) => `${missing} ${position}`)
          .join(', ');

  return (
    <section
      className="my-4 rounded-lg border-2 border-dashed border-neutral-400 bg-neutral-100 p-3"
      data-cy="live-simulation-harness"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold">
          Live simulation harness
          {activeSquad && (
            <span className="ml-2 rounded bg-secondary-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
              Driving this pitch
            </span>
          )}
        </p>
        <div className="flex gap-2">
          {activeSquad && (
            <button
              type="button"
              className="rounded border border-neutral-400 px-2 py-1 text-xs"
              data-cy="harness-clear"
              onClick={() => applySquad(null)}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className="rounded border border-neutral-400 px-2 py-1 text-xs"
            data-cy="harness-toggle"
            aria-expanded={isOpen}
            onClick={() => setIsOpen((previous) => !previous)}
          >
            {isOpen ? 'Close' : 'Build a team'}
          </button>
        </div>
      </div>

      {builder.attached.length > 0 && (
        <ul
          className="mt-2 flex flex-wrap gap-2 text-xs"
          data-cy="harness-attached"
        >
          {builder.attached.map((fixture) => (
            <li
              key={fixture.matchId}
              className="rounded bg-white px-2 py-1"
              data-cy="harness-attached-fixture"
            >
              {fixture.homeTeam} {fixture.score} {fixture.awayTeam} &middot;{' '}
              {fixture.status}
              {fixture.minute !== null && ` ${fixture.minute}'`}
              {fixture.provenance && ` · ${fixture.provenance}`}
            </li>
          ))}
        </ul>
      )}

      {isOpen && (
        <div className="mt-3 flex flex-col gap-3">
          <label className="flex items-center gap-2 text-sm">
            Gameweek
            <input
              type="number"
              min={1}
              max={38}
              value={builder.gameweek}
              data-cy="harness-gameweek"
              className="w-16 rounded border border-neutral-300 px-1 py-0.5 text-sm"
              onChange={(event) =>
                builder.setGameweek(Number(event.target.value))
              }
            />
          </label>

          {builder.isLoadingFixtures && (
            <p className="text-sm text-neutral-600">Loading fixtures…</p>
          )}
          {builder.fixturesError && (
            <p className="text-sm text-danger-600" data-cy="harness-error">
              {builder.fixturesError}
            </p>
          )}
          {!builder.isLoadingFixtures && !builder.fixturesError && (
            <FixtureList
              fixtures={builder.fixtures}
              selectedMatchIds={builder.selectedMatchIds}
              onToggle={builder.toggleMatch}
            />
          )}

          {builder.rosters.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded bg-neutral-800 px-2 py-1 text-xs text-white"
                data-cy="harness-fill"
                onClick={builder.fill}
              >
                Fill from selected fixtures
              </button>
              <span className="text-xs text-neutral-600">
                {builder.rosters.reduce(
                  (total, roster) => total + roster.players.length,
                  0
                )}{' '}
                selectable players,{' '}
                {builder.rosters.reduce(
                  (total, roster) => total + roster.unidentifiedCount,
                  0
                )}{' '}
                without an FPL id
              </span>
            </div>
          )}

          {shortfallText && (
            <p className="text-sm text-danger-600" data-cy="harness-shortfall">
              Not enough players in the selected fixtures: short {shortfallText}
              . Select another fixture.
            </p>
          )}

          {draft && (
            <>
              <label className="flex items-center gap-2 text-sm">
                Chip
                <select
                  value={String(draft.activeChip)}
                  data-cy="harness-chip"
                  className="rounded border border-neutral-300 px-1 py-0.5 text-sm"
                  onChange={(event) =>
                    builder.setDraft(
                      setActiveChip(draft, toChip(event.target.value))
                    )
                  }
                >
                  {CHIPS.map((chip) => (
                    <option key={String(chip.value)} value={String(chip.value)}>
                      {chip.label}
                    </option>
                  ))}
                </select>
              </label>

              <SquadEditor
                squad={draft}
                rosters={builder.rosters}
                onChange={builder.setDraft}
              />

              {problems.length > 0 && (
                <ul
                  className="flex flex-col gap-0.5 text-sm text-danger-600"
                  data-cy="harness-problems"
                >
                  {problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              )}

              <button
                type="button"
                className="self-start rounded bg-secondary-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
                data-cy="harness-apply"
                disabled={problems.length > 0}
                onClick={() => applySquad(draft)}
              >
                Use this team
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
};
